import { useEffect, useState, useCallback } from 'react';
import { settingsService } from '../services/settingsService';
import { usersService } from '../services/usersService';

/**
 * Loads the org's user-defined lead sources and lets the user add new ones.
 * Built-in sources (website/referral/…) stay hardcoded in each dropdown; this
 * only supplies the *custom* values so the source list is self-extensible (#37).
 */
export function useCustomLeadSources() {
  const [customSources, setCustomSources] = useState<string[]>([]);
  const [orgId, setOrgId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const id = await usersService.getCurrentUserOrgId();
        if (!active || !id) return;
        setOrgId(id);
        const settings = await settingsService.getOrganizationSettings(id);
        if (active) setCustomSources(settings.customLeadSources || []);
      } catch (err) {
        console.error('Failed to load custom lead sources:', err);
      }
    })();
    return () => { active = false; };
  }, []);

  /** Persist a new source and return its trimmed value (empty string if invalid). */
  const addSource = useCallback(async (name: string): Promise<string> => {
    const value = (name || '').trim();
    if (!value || !orgId) return '';
    try {
      await settingsService.addLeadSource(orgId, value, 'user');
      setCustomSources(prev => (prev.includes(value) ? prev : [...prev, value].sort()));
    } catch (err) {
      console.error('Failed to add lead source:', err);
    }
    return value;
  }, [orgId]);

  return { customSources, addSource };
}
